/**
 * OverdraftBanner — F-009.3.
 *
 * Banner di stato del wallet, derivato dal summary:
 *  - wallet bloccato            → Alert destructive (registrazione disabilitata)
 *  - disponibilità < 0 (oltre fido) → Alert destructive (ricarica per continuare)
 *  - saldo < 20% del fido       → Alert warning (ricarica consigliata)
 *  - altrimenti                 → nessun banner
 *
 * WCAG: lo stato è veicolato da titolo + icona, mai dal solo colore.
 */
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import type { WalletSummaryResponse } from '@app/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { centsToNumber } from '../lib/format';

export type WalletBannerKind = 'blocked' | 'over' | 'low' | null;

/** Logica pura (testabile in isolamento) per scegliere il banner da mostrare. */
export function selectBanner(summary: WalletSummaryResponse): WalletBannerKind {
  if (summary.walletBlocked) return 'blocked';
  const balance = centsToNumber(summary.balanceCents);
  const fido = centsToNumber(summary.fidoLimitCents);
  const available = centsToNumber(summary.availableCents);
  if (available < 0) return 'over';
  if (fido > 0 && balance >= 0 && balance < fido * 0.2) return 'low';
  return null;
}

export function OverdraftBanner({ summary }: { summary: WalletSummaryResponse }) {
  const kind = selectBanner(summary);
  if (!kind) return null;

  if (kind === 'low') {
    return (
      <Alert data-banner="low">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Saldo in esaurimento</AlertTitle>
        <AlertDescription>
          Il saldo è basso rispetto al tuo fido. Ti consigliamo di ricaricare per non
          interrompere la registrazione delle pratiche.
        </AlertDescription>
      </Alert>
    );
  }

  // 'blocked' | 'over' — entrambi destructive con copy dedicato.
  const isBlocked = kind === 'blocked';
  return (
    <Alert variant="destructive" data-banner={kind}>
      <ShieldAlert className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>{isBlocked ? 'Wallet bloccato' : 'Fido superato'}</AlertTitle>
      <AlertDescription>
        {isBlocked
          ? 'Il tuo wallet è bloccato: non puoi registrare nuove pratiche finché non viene sbloccato. Contatta il supporto.'
          : 'Hai superato il fido disponibile. Ricarica il wallet per poter registrare nuove pratiche.'}
      </AlertDescription>
    </Alert>
  );
}
